import React, { useState } from 'react';

export interface DocumentItem {
  id: string;
  icon: string;
  title: string;
  description?: string;
  type: 'pdf-filled' | 'pdf-static';
  /** Download URL for static PDFs */
  url?: string;
}

interface DocumentListProps {
  documents: DocumentItem[];
  /** Content to render inside the expanded "pdf-filled" viewer (e.g. an <iframe> for the filled agreement) */
  renderFilledViewer?: () => React.ReactNode;
  /** Optional details bar rendered above the filled PDF viewer */
  renderFilledDetails?: () => React.ReactNode;
}

/**
 * Expandable document list with inline PDF preview.
 * Extracted from ReviewSign — displays agreement + supplementary documents.
 */
const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  renderFilledViewer,
  renderFilledDetails,
}) => {
  const [expandedDoc, setExpandedDoc] = useState<string | null>('agreement');

  return (
    <div style={{
      background: '#fff',
      border: '1.5px solid #E2E8F0',
      borderRadius: 16,
      padding: '20px 24px',
      marginBottom: 16,
      boxShadow: '0 2px 12px rgba(27,58,107,0.06)',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center',
        marginBottom: 16, paddingBottom: 14,
        borderBottom: '1px solid #F1F5F9',
      }}>
        <h3 style={{
          margin: 0, fontSize: 14, fontWeight: 700,
          color: '#1B3A6B', textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          Agreement Documents
        </h3>
        <span style={{
          marginLeft: 'auto', fontSize: 11, fontWeight: 600,
          color: '#94A3B8',
        }}>
          {documents.length} documents
        </span>
      </div>

      {/* Documents */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {documents.map((doc) => (
          <div key={doc.id}>
            {/* Row */}
            <div
              onClick={() => setExpandedDoc(expandedDoc === doc.id ? null : doc.id)}
              className="doc-list__row"
              style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px',
                background: expandedDoc === doc.id ? '#F0F7FF' : '#F8FAFC',
                border: `1px solid ${expandedDoc === doc.id ? '#BFDBFE' : '#E2E8F0'}`,
                borderRadius: expandedDoc === doc.id ? '10px 10px 0 0' : 10,
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 20, flexShrink: 0 }}>{doc.icon}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: '#1E293B', fontSize: 14, fontWeight: 600 }}>{doc.title}</div>
                {doc.description && (
                  <div style={{ color: '#94A3B8', fontSize: 12, marginTop: 2 }}>{doc.description}</div>
                )}
              </div>
              <div className="doc-list__actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: '#0F2647',
                  background: '#E0EDFF', padding: '4px 10px',
                  borderRadius: 20, textTransform: 'uppercase',
                }}>Review</span>
                <span style={{
                  fontSize: 14, color: '#64748B',
                  transform: expandedDoc === doc.id ? 'rotate(180deg)' : 'rotate(0)',
                  transition: 'transform 0.2s ease',
                  display: 'inline-block',
                }}>▼</span>
              </div>
            </div>

            {/* Expanded: Filled PDF */}
            {doc.type === 'pdf-filled' && expandedDoc === doc.id && (
              <div style={{
                border: '1px solid #BFDBFE',
                borderTop: 'none',
                borderRadius: '0 0 10px 10px',
                overflow: 'hidden',
                background: '#fff',
              }}>
                {renderFilledDetails?.()}
                {renderFilledViewer?.()}
              </div>
            )}

            {/* Expanded: Static PDF */}
            {doc.type === 'pdf-static' && expandedDoc === doc.id && (
              <div style={{
                border: '1px solid #BFDBFE',
                borderTop: 'none',
                borderRadius: '0 0 10px 10px',
                overflow: 'hidden',
                background: '#fff',
              }}>
                {doc.url ? (
                  <iframe
                    src={doc.url}
                    title={doc.title}
                    style={{ width: '100%', height: 500, border: 'none', display: 'block' }}
                  />
                ) : (
                  <div style={{
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    height: 200, color: '#64748B', fontSize: 13, gap: 8,
                  }}>
                    <span style={{ fontSize: 28 }}>📄</span>
                    Document preview unavailable — please contact support.
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DocumentList;
